/**
 * useNumberingBaseFieldOptions — buduje listę opcji dropdowna „Pole daty numeracji"
 * dla edytora typu dokumentu (`NumberingMaskEditor`).
 *
 * - Pola CORE (bazowe/klasowe) — ze wspólnej stałej `DOCUMENT_CORE_DATE_FIELDS`
 *   (etykiety i18n), zawsze; pierwsze = pole domyślne klasy.
 * - Pola DODATKOWE (ext) typu data/czas — z katalogu kartoteki (`useFieldsForModule`),
 *   TYLKO w scope tenanta (`includeExt`). Panel ITE (typy globalne) korzysta z INNEGO
 *   katalogu dodatkowego (centralny ADM, `system_kartoteka_fields`) — patrz
 *   `buildGlobalNumberingBaseFieldOptions` poniżej, `includeExt` tutaj zawsze `false`.
 *
 * Pola ext o slugu pokrywającym się z core są pomijane (core bierzemy ze stałej,
 * dla spójnych etykiet i18n).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_CORE_DATE_FIELDS, type DocumentClass } from '@saas/shared';
import type { TFunction } from 'i18next';

import {
  DOCUMENTS_TARGET_MODULE_CODE,
  useFieldsForModule,
} from '@/components/process-builder/useFieldsForModule';

export interface NumberingBaseFieldOption {
  value: string;
  label: string;
}

const DATE_FIELD_TYPES = new Set(['date', 'timestamp']);

export function useNumberingBaseFieldOptions(
  documentClass: DocumentClass,
  includeExt: boolean,
): NumberingBaseFieldOption[] {
  const { t } = useTranslation();
  // Ext-daty tylko w scope tenanta; bez includeExt query się nie odpala (moduleSlug=null).
  const { data } = useFieldsForModule(
    includeExt ? DOCUMENTS_TARGET_MODULE_CODE : null,
    documentClass,
  );

  return useMemo(() => {
    const coreFields = DOCUMENT_CORE_DATE_FIELDS[documentClass];
    const coreSlugs = new Set(coreFields.map((f) => f.slug));
    const coreOptions: NumberingBaseFieldOption[] = coreFields.map((f) => ({
      value: f.slug,
      label: t(f.labelKey),
    }));
    const extOptions: NumberingBaseFieldOption[] = (data?.fields ?? [])
      .filter((f) => DATE_FIELD_TYPES.has(f.fieldType) && !coreSlugs.has(f.slug))
      .map((f) => ({ value: f.slug, label: f.name }));
    return [...coreOptions, ...extOptions];
  }, [documentClass, data, t]);
}

/** Pole kartoteki (kształt zwrócony przez `GET /admin/kartoteki/:slug/definition`) — duck-typed,
 * żeby ten plik nie zależał od konkretnego typu `KartotekaBuilderField` z `lib/kartoteki`. */
export interface CentralKartotekaDateFieldLike {
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
}

/**
 * buildGlobalNumberingBaseFieldOptions — wariant CENTRALNY (ADM) dla edytora typu
 * globalnego (panel ITE: `AdminGlobalDocumentTypes`, `DocumentTypeEditorModal`).
 *
 * W przeciwieństwie do `useNumberingBaseFieldOptions` (scope tenanta, fetch z
 * TENANTOWEGO `/system-modules/:slug/fields`) — panel ITE nie ma bazy tenanta do
 * zapytania; jego pola dodatkowe pochodzą z CENTRALNEGO katalogu ADM
 * (`system_kartoteka_fields`), już pobranego przez wołającego przez
 * `useKartotekaDefinition` (`GET /admin/kartoteki/:slug/definition`). Czysta funkcja
 * (bez własnego hooka/fetchu) — wołający decyduje KTÓRY wynik definicji podać:
 * widok klasy (pola wspólne, `typeId` pominięte — zgodne z regułą walidacji POST)
 * albo widok typu (`?typeId=`, pola wspólne ∪ prywatne tego typu — zgodne z PUT).
 *
 * `centralFields` — `undefined` w trakcie ładowania/przy błędzie fetcha → degradacja
 * do samego core (jak `includeExt=false` w wariancie tenantowym).
 */
export function buildGlobalNumberingBaseFieldOptions(
  documentClass: DocumentClass,
  centralFields: readonly CentralKartotekaDateFieldLike[] | undefined,
  t: TFunction,
): NumberingBaseFieldOption[] {
  const coreFields = DOCUMENT_CORE_DATE_FIELDS[documentClass];
  const coreSlugs = new Set(coreFields.map((f) => f.slug));
  const coreOptions: NumberingBaseFieldOption[] = coreFields.map((f) => ({
    value: f.slug,
    label: t(f.labelKey),
  }));
  const additionalOptions: NumberingBaseFieldOption[] = (centralFields ?? [])
    .filter((f) => !f.isCore && DATE_FIELD_TYPES.has(f.fieldType) && !coreSlugs.has(f.slug))
    .map((f) => ({ value: f.slug, label: f.name }));
  return [...coreOptions, ...additionalOptions];
}
